/**
 * Front's REST transport, in ONE place.
 *
 * Every raw call to our API (the ones that don't go through the typed
 * `api` client in `lib/api.ts`) shares TWO decisions, and neither belongs to
 * the calling module:
 *
 *   1. **How it authenticates.** Web = HttpOnly `space.device` cookie with
 *      `credentials: "include"` + `X-Active-Account` selector (+
 *      `X-Impersonation-Token` if applicable). Tauri = NOTHING in JS: the Bearer is
 *      injected by the `api_request` Rust proxy, reached via
 *      {@link platformApiFetch}. That asymmetry is resolved by `apiAuthHeaders()`
 *      (which returns `{}` on native) — copying it by hand is exactly what broke
 *      the desktop app.
 *   2. **How a server error becomes a message.** 401 =
 *      expired session (actionable), otherwise the JSON body's `message`, otherwise
 *      `HTTP <status>`; and the code stays on `.status` for whoever needs it.
 *
 * It used to be written thirteen times: nine `features/` modules with their own
 * `authHeaders()` + bare `fetch` (which on Tauri carried NO credential at all,
 * because they neither went through the Rust proxy nor could: the token doesn't live in JS), and
 * four with their own `jsonFetch`. The nine copies, on top of that, gave FIVE
 * different messages for the same 401.
 */
import { apiAuthHeaders, platformApiFetch, SERVER_BASE_URL } from "@/lib/api";

/** API origin without a trailing slash. This module's `path`s hang off this. */
export const API_ORIGIN = SERVER_BASE_URL.replace(/\/$/, "");

/** Error from a non-ok response, with the HTTP code accessible. */
export interface ApiRequestError extends Error {
  status: number;
}

/**
 * Message from a non-ok response. Consumes the body, so it's only called
 * once it's already known the response failed.
 */
function withStatus(error: Error, status: number): ApiRequestError {
  return Object.assign(error, { status });
}

async function errorFrom(response: Response): Promise<ApiRequestError> {
  if (response.status === 401) {
    // Session expired mid-action: actionable message, not "HTTP 401"
    // (the auth shell redirects to login on the next navigation).
    return withStatus(
      new Error("Session expired: refresh the page and log in again"),
      response.status,
    );
  }
  let message = `HTTP ${response.status}`;
  try {
    const body: unknown = await response.json();
    if (
      body &&
      typeof body === "object" &&
      "message" in body &&
      typeof body.message === "string" &&
      body.message
    ) {
      ({ message } = body);
    }
  } catch {
    // no JSON body: it keeps the `HTTP <status>`
  }
  return withStatus(new Error(message), response.status);
}

/**
 * Reads the JSON body of a response, throwing {@link ApiRequestError} if it's
 * not ok. A 204 / empty body resolves to `undefined`.
 *
 * Public because there's one surface —the assistant's public `ask`— that
 * deliberately does NOT send credentials (it's anonymous) but does share the
 * error handling; that's the only legitimate case for bypassing {@link apiFetch}.
 */
export async function readApiJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw await errorFrom(response);
  }
  const text = await response.text();
  // The raw client's only untyped boundary: the server is the authority on the shape.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Authenticated `fetch` against the API. `path` is relative to the API origin
 * (e.g. `/api/crm/leads`). Returns the raw `Response` for whoever needs
 * headers or a non-JSON body.
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // Multipart: the browser sets the `Content-Type`, because only it knows the
  // `boundary` the server's parser needs. Setting it by hand breaks the upload.
  const isMultipart = typeof FormData !== "undefined" && init.body instanceof FormData;
  return platformApiFetch(`${API_ORIGIN}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(isMultipart ? {} : { "Content-Type": "application/json" }),
      ...apiAuthHeaders(),
      ...init.headers,
    },
  });
}

/** Authenticated JSON call: the default path for every REST client. */
export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  return readApiJson<T>(await apiFetch(path, init));
}
